import { useEffect, useMemo } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import JsonLd from '@/components/base/JsonLd';
import { useSeo, SITE_URL, SITE_LOGO } from '@/hooks/useSeo';
import { pillarArticles } from '@/mocks/articles';

const MAIN_URL = 'https://kaspreadsheet.com';

export default function PillarArticle() {
  const { t, i18n } = useTranslation();
  const { slug: paramSlug } = useParams<{ slug: string }>();
  const location = useLocation();
  const slug = useMemo(() => {
    if (paramSlug) return paramSlug;
    // Fallback: extract slug from pathname like /allchinabuy-tracking
    return location.pathname.replace(/^\//, '').split('/').filter(Boolean).pop() || '';
  }, [paramSlug, location.pathname]);

  const article = useMemo(() => pillarArticles.find((a) => a.slug === slug), [slug]);

  const lang = i18n.language?.split('-')[0] ?? 'en';
  const data = article ? (article as Record<string, unknown>)[lang] as { title: string; metaTitle: string; metaDescription: string; intro: string; sections: { heading: string; body: string }[]; faq: { q: string; a: string }[] } : undefined;

  useSeo({
    title: data?.metaTitle ?? 'Not Found',
    description: data?.metaDescription ?? 'Article not found.',
    keywords: article
      ? `AllChinaBuy ${article.slug.split('-').slice(1).join(' ')}, AllChinaBuy spreadsheet, buying guide`
      : 'AllChinaBuy spreadsheet, buying guide',
    path: `/${slug}`,
    image: article?.image,
    lang: i18n.language,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article || !data) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-24">
          <div className="mx-auto max-w-3xl px-4 py-20 text-center md:px-6">
            <h1 className="font-heading text-3xl font-bold text-foreground-950">Not Found</h1>
            <p className="mt-4 text-foreground-600">Article not found.</p>
            <Link
              to="/"
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition hover:bg-primary-600"
            >
              {t('cta.backHome')}
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: data.title,
    description: data.metaDescription,
    image: [article.image],
    inLanguage: lang,
    datePublished: '2026-01-01',
    dateModified: '2026-08-24',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${SITE_URL}/${article.slug}`,
    },
    author: {
      '@type': 'Organization',
      name: 'AllChinaBuy Spreadsheet',
    },
    publisher: {
      '@type': 'Organization',
      name: 'AllChinaBuy Spreadsheet',
      logo: { '@type': 'ImageObject', url: SITE_LOGO },
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={jsonLd} />
      <Navbar />
      <main className="pt-24">
        <article className="mx-auto max-w-3xl px-4 pb-16 md:px-6">
          <nav aria-label="Breadcrumb" className="text-sm text-foreground-500">
            <ol className="flex flex-wrap items-center gap-1.5">
              <li>
                <Link to="/" className="text-foreground-600 transition hover:text-primary-700">
                  {t('nav.home')}
                </Link>
              </li>
              <li aria-hidden="true">/</li>
              <li aria-current="page" className="text-foreground-400">
                {data.title}
              </li>
            </ol>
          </nav>

          <header className="mt-6">
            <span className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
              {t('home.hero.badge')}
            </span>
            <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
              {data.title}
            </h1>
            <p className="mt-5 text-base leading-relaxed text-foreground-700 md:text-lg">
              {data.intro}
            </p>
          </header>

          {article.image && (
            <div className="mt-8 overflow-hidden rounded-lg border border-background-200">
              <img
                src={article.image}
                alt={data.title}
                className="h-64 w-full object-cover object-top md:h-80"
              />
            </div>
          )}

          {data.sections.map((section, idx) => (
            <section key={idx} className="mt-10">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                {section.heading}
              </h2>
              <p className="mt-4 text-base leading-relaxed text-foreground-700">
                {section.body}
              </p>
            </section>
          ))}

          <section className="mt-10">
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              FAQ
            </h2>
            <div className="mt-5 space-y-4">
              {data.faq.map((item, idx) => (
                <div key={idx} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <h3 className="text-base font-semibold text-foreground-900">{item.q}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-600">{item.a}</p>
                </div>
              ))}
            </div>
          </section>

          <footer className="mt-12 rounded-lg border border-background-200 bg-accent-50 p-6 text-center">
            <h2 className="font-heading text-xl font-bold text-foreground-950">
              {t('article.cta_title')}
            </h2>
            <p className="mx-auto mt-2 max-w-md text-sm text-foreground-600">
              {t('article.cta_text')}
            </p>
            <div className="mt-5 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <a
                href={MAIN_URL}
                target="_blank"
                rel="noopener nofollow"
                className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition hover:bg-primary-600"
              >
                {t('cta.spreadsheet')}
              </a>
              <Link
                to="/"
                className="whitespace-nowrap rounded-md border border-background-300 px-6 py-2.5 text-sm font-semibold text-foreground-800 transition hover:bg-background-100"
              >
                {t('article.backHome')}
              </Link>
            </div>
          </footer>
        </article>
      </main>
      <Footer />
    </div>
  );
}